import React, { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { FilterActionsFooter } from "@design-system/Filter/FilterActionsFooter/FilterActionsFooter";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { BaseFilterProps } from "../../lib";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";

export const TeamMembersFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { data: organizationMembers } = useCurrentOrganizationMembers();

    const options = useMemo(
        (): FilterMultiselectOption[] =>
            (organizationMembers ?? []).map((member) => ({
                value: member.user.id,
                label: member.user.name,
                searchableLabel: member.user.email
                    ? `${member.user.name} ${member.user.email}`
                    : member.user.name,
            })),
        [organizationMembers],
    );

    const value = filters.teamMemberIds ?? [];

    const handleChange = useCallback(
        (val: FilterMultiselectValue[]) => {
            onChange({
                teamMemberIds: val.length > 0 ? (val as number[]) : undefined,
            });
        },
        [onChange],
    );

    const reverseSelection = useCallback(() => {
        if (filters.teamMemberIds && organizationMembers) {
            onChange({
                teamMemberIds: organizationMembers
                    .filter(
                        (member) =>
                            !filters.teamMemberIds?.includes(member.user.id),
                    )
                    .map(({ user }) => user.id),
            });
        }
    }, [organizationMembers, onChange, filters.teamMemberIds]);

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={handleChange}
                name="teamMemberIds"
                onBlur={noop}
                filterFn={(search, option) => {
                    const query = search.toLowerCase();
                    const searchTarget =
                        option.searchableLabel ?? String(option.label ?? "");
                    return searchTarget.toLowerCase().includes(query);
                }}
            />
            {!!filters.teamMemberIds?.length && (
                <FilterActionsFooter
                    deselectFunction={() =>
                        onChange({ teamMemberIds: undefined })
                    }
                    selectedCount={filters.teamMemberIds.length}
                    reverseFunction={reverseSelection}
                />
            )}
        </div>
    );
};
